
import { defineComponent, onMounted, computed, watch } from 'vue'
import UIkit from 'uikit'
import type { PropType } from 'vue'
import qs from 'qs'
import store from '@/store'
import { useRouter } from 'vue-router'
import { createNamespacedHelpers } from 'vuex-composition-helpers'
import { useModalConfirm } from '@/utils/useModalDialogs'
import { notify } from '@/utils/useNotifications'
import { KindType } from '@/models/templates/Template.interface'
import { ImageInterface } from '@/models/images/Image.interface'
import DefaultLayout from '@/layouts/DefaultLayout.vue'
import TemplateTabs from '@/components/IndexTemplates/TemplateTabs.vue'
import TemplateActions from '@/components/IndexTemplates/TemplateActions.vue'
import ImageActions from '@/components/IndexTemplates/ImageActions.vue'
import TemplatesPanel from '@/components/IndexTemplates/Panels/TemplatesPanel.vue'
import ImagesPanel from '@/components/IndexTemplates/Panels/ImagesPanel.vue'
import TreePanel from '@/components/IndexTemplates/Panels/TreePanel.vue'
import TemplateSlugModal from '@/components/Shared/Modals/TemplateSlugModal.vue'
import { useFeatureAccess } from '@/composables/useFeatureAccess'

export default defineComponent({
  components: {
    DefaultLayout,
    TemplateTabs,
    TemplateActions,
    ImageActions,
    TemplatesPanel,
    ImagesPanel,
    TreePanel,
    TemplateSlugModal
  },
  props: {
    kind: { type: String as PropType<KindType>, default: 'email' }
  },
  setup(props) {
    const itemTreeView = computed(() => store.state.tree.treeView)
    const itemTreeLoading = computed(() => store.state.tree.loading)
    const currentKind = computed(
      () => store.state.templates.filterAndSearch.kind
    )
    const isImageKind = computed(() => currentKind.value === 'image')
    const { canWrite } = useFeatureAccess(() => currentKind.value ?? '')

    const router = useRouter()

    const { useActions: useTemplateActions } =
      createNamespacedHelpers('templates')
    const { useActions: useTreeActions } = createNamespacedHelpers('tree')
    const { useActions: useImageActions } = createNamespacedHelpers('images')

    const { setSlug, setFilters, setFiltersAndLoad, loadTemplates } =
      useTemplateActions([
        'setSlug',
        'setFilters',
        'setFiltersAndLoad',
        'loadTemplates'
      ])
    const { toggleTreeView, loadEditorVariables } = useTreeActions([
      'toggleTreeView',
      'loadEditorVariables'
    ])
    const { loadImages, destroyImage } = useImageActions([
      'loadImages',
      'destroyImage'
    ])

    const tabClickHandler = async (kind: KindType) => {
      if (currentKind.value === kind) return

      await setFiltersAndLoad({ kind })

      window.history.replaceState(
        history.state,
        '',
        `?${qs.stringify({ kind })}`
      )

      if (itemTreeView.value) loadEditorVariables({ kind })
    }
    const imageUploadedHandler = () => {
      if (itemTreeView.value) loadEditorVariables({ kind: 'image' })
    }

    const deleteImageHandler = (image: ImageInterface) => {
      useModalConfirm(
        `Are you sure you want to delete the "${image.title}" image?`,
        async () => {
          await destroyImage(image.id).then(() =>
            notify('Image deleted', 'success')
          )
          loadImages()
        },
        () => undefined
      )
    }

    const showModal = (elId: string) => {
      const modal = document.getElementById(elId)
      if (modal) UIkit.modal(modal).show()
    }

    const createNewTemplateHandler = () => showModal('template-slug-modal')

    const submitNewTemplateSlugHandler = (slug: string) => {
      if (!canWrite.value) return

      setSlug(slug)
      router.push({
        name: 'new_template',
        params: { kind: currentKind.value }
      })
    }

    const initHandler = () => {
      setFilters({ kind: props.kind })

      if (currentKind.value === 'image') {
        loadImages()
      } else {
        loadTemplates()
      }
    }

    onMounted(initHandler)
    watch(props, initHandler)

    return {
      itemTreeView,
      itemTreeLoading,
      currentKind,
      isImageKind,
      canWrite,
      tabClickHandler,
      toggleTreeView,
      imageUploadedHandler,
      deleteImageHandler,
      createNewTemplateHandler,
      submitNewTemplateSlugHandler,
      loadTemplates,
      loadImages,
      loadEditorVariables
    }
  }
})
